import React, { useState } from 'react';
import { UserProfile, Address } from '../../types/user';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/database';
import { toast } from 'react-toastify';
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from 'use-places-autocomplete';
import Input from '../common/Input';
import './EditProfileDialog.css';

interface EditProfileDialogProps {
  userProfile: UserProfile;
  onClose: () => void;
  onSave: () => void;
}

interface FormData {
  name: string;
  phone: string;
  shippingAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    latitude: number | null;
    longitude: number | null;
  };
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
  userProfile,
  onClose,
  onSave,
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: userProfile.name || '',
    phone: userProfile.phone || '',
    shippingAddress: {
      street: userProfile.shippingAddress?.street || '',
      city: userProfile.shippingAddress?.city || '',
      state: userProfile.shippingAddress?.state || '',
      zipCode: userProfile.shippingAddress?.zipCode || '',
      country: userProfile.shippingAddress?.country || 'Ireland',
      latitude: userProfile.shippingAddress?.latitude || null,
      longitude: userProfile.shippingAddress?.longitude || null,
    },
  });

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'ie' },
    },
    debounce: 800,
  });

  // Debug logging
  console.log('[EditProfileDialog] Places Autocomplete Status:', {
    ready,
    value,
    status,
    suggestionsCount: data?.length || 0,
  });

  const parseAddressComponents = (components: any[]): Partial<Address> => {
    const parsed: any = {
      street: '',
      city: '',
      state: '',
      zipCode: '',
    };

    let streetNumber = '';
    let route = '';

    components.forEach((component) => {
      const types = component.types;

      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        parsed.city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        parsed.state = component.long_name;
      }
      if (types.includes('postal_code')) {
        parsed.zipCode = component.long_name;
      }
    });

    // Combine street number and route
    if (streetNumber && route) {
      parsed.street = `${streetNumber} ${route}`;
    } else if (route) {
      parsed.street = route;
    }

    return parsed;
  };

  const handleSelectAddress = async (description: string) => {
    console.log('[EditProfileDialog] Address selected:', description);
    setValue(description, false);
    clearSuggestions();

    try {
      console.log('[EditProfileDialog] Fetching geocode for:', description);
      const results = await getGeocode({ address: description });
      console.log('[EditProfileDialog] Geocode results:', results);

      const { lat, lng } = await getLatLng(results[0]);
      console.log('[EditProfileDialog] Coordinates:', { lat, lng });

      const addressComponents = results[0].address_components;
      console.log('[EditProfileDialog] Address components:', addressComponents);

      const parsed = parseAddressComponents(addressComponents);
      console.log('[EditProfileDialog] Parsed address:', parsed);

      setFormData({
        ...formData,
        shippingAddress: {
          street: parsed.street || '',
          city: parsed.city || '',
          state: parsed.state || '',
          zipCode: parsed.zipCode || '',
          country: 'Ireland',
          latitude: lat,
          longitude: lng,
        },
      });

      console.log('[EditProfileDialog] Form data updated with coordinates');

      // Clear the autocomplete input
      setValue('', false);
    } catch (error) {
      console.error('[EditProfileDialog] Address selection error:', error);
      toast.error('Failed to get address details');
    }
  };

  const geocodeManualAddress = async (): Promise<{ lat: number; lng: number } | null> => {
    const { street, city } = formData.shippingAddress;

    console.log('[EditProfileDialog] Geocoding manual address:', { street, city });

    if (!street || !city) {
      console.log('[EditProfileDialog] Missing street or city, skipping geocoding');
      return null;
    }

    try {
      const fullAddress = `${street}, ${city}, Ireland`;
      console.log('[EditProfileDialog] Full address for geocoding:', fullAddress);

      const results = await getGeocode({ address: fullAddress });
      console.log('[EditProfileDialog] Manual geocode results:', results);

      const { lat, lng } = await getLatLng(results[0]);
      console.log('[EditProfileDialog] Manual geocode coordinates:', { lat, lng });

      return { lat, lng };
    } catch (error) {
      console.error('[EditProfileDialog] Geocoding error:', error);
      return null;
    }
  };

  const handleSave = async () => {
    console.log('[EditProfileDialog] Save button clicked');
    console.log('[EditProfileDialog] Current form data:', formData);

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Phone is required');
      return;
    }

    if (!formData.shippingAddress.street.trim()) {
      toast.error('Street address is required');
      return;
    }

    if (!formData.shippingAddress.city.trim()) {
      toast.error('City is required');
      return;
    }

    setLoading(true);

    try {
      // Geocode if no coordinates (user entered manually)
      let coordinates = {
        latitude: formData.shippingAddress.latitude,
        longitude: formData.shippingAddress.longitude,
      };

      console.log('[EditProfileDialog] Current coordinates:', coordinates);

      if (!coordinates.latitude || !coordinates.longitude) {
        console.log('[EditProfileDialog] No coordinates, attempting geocoding...');
        const geocoded = await geocodeManualAddress();

        if (geocoded) {
          coordinates = {
            latitude: geocoded.lat,
            longitude: geocoded.lng,
          };
          console.log('[EditProfileDialog] Geocoding successful, new coordinates:', coordinates);
        } else {
          console.log('[EditProfileDialog] Geocoding failed or skipped');
        }
      }

      // Update profile in Firebase
      const updatedProfile: Partial<UserProfile> = {
        name: formData.name,
        phone: formData.phone,
        shippingAddress: {
          street: formData.shippingAddress.street,
          city: formData.shippingAddress.city,
          state: formData.shippingAddress.state,
          zipCode: formData.shippingAddress.zipCode,
          country: 'Ireland',
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
        },
      };

      console.log('[EditProfileDialog] Updating profile with:', updatedProfile);
      await updateUserProfile(currentUser!.uid, updatedProfile);

      console.log('[EditProfileDialog] Profile updated successfully');
      toast.success('Profile updated successfully');
      onSave();
    } catch (error: any) {
      console.error('[EditProfileDialog] Save error:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); // Match animation duration
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'name' || field === 'phone') {
      setFormData({ ...formData, [field]: value });
    } else {
      // Address fields
      setFormData({
        ...formData,
        shippingAddress: {
          ...formData.shippingAddress,
          [field]: field === 'zipCode' ? value.toUpperCase() : value,
        },
      });
    }
  };

  return (
    <div className={`edit-dialog-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`edit-dialog ${isClosing ? 'closing' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="edit-dialog-header">
          <h2 className="edit-dialog-title">Edit Profile</h2>
          <button className="edit-dialog-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="edit-dialog-content">
          {/* Section 1: Personal Information */}
          <div className="edit-section">
            <h3 className="edit-section-title">Personal Information</h3>

            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              icon={<img src="/assets/images/profile2.png" alt="" style={{ width: '20px', height: '20px' }} />}
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="+353 XX XXX XXXX"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              icon={<img src="/assets/images/phone.png" alt="" style={{ width: '20px', height: '20px' }} />}
            />
          </div>

          {/* Section 2: Shipping Address */}
          <div className="edit-section">
            <h3 className="edit-section-title">Shipping Address</h3>

            {/* Google Places Autocomplete */}
            <div className="autocomplete-section">
              <label className="autocomplete-label">Search Address</label>
              <div style={{ position: 'relative' }}>
                <div className="autocomplete-wrapper">
                  <span className="autocomplete-icon">🔍</span>
                  <input
                    type="text"
                    className="autocomplete-input"
                    placeholder="Start typing an Irish address..."
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={!ready}
                  />
                </div>

                {/* Suggestions Dropdown */}
                {status === 'OK' && data.length > 0 && (
                  <div className="suggestions-dropdown">
                    {data.map((suggestion) => (
                      <div
                        key={suggestion.place_id}
                        className="suggestion-item"
                        onClick={() => handleSelectAddress(suggestion.description)}
                      >
                        <span className="suggestion-icon">📍</span>
                        <span className="suggestion-text">{suggestion.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="address-divider">
              <span className="divider-line"></span>
              <span className="divider-text">OR</span>
              <span className="divider-line"></span>
            </div>

            {/* Manual Entry Section */}
            <div className="manual-entry-section">
              <p className="manual-entry-label">Enter Manually</p>

              {/* Address Fields */}
              <Input
                label="Street Address*"
                type="text"
                placeholder="123 Main Street"
                value={formData.shippingAddress.street}
                onChange={(e) => handleInputChange('street', e.target.value)}
              />

              <Input
                label="City/Town*"
                type="text"
                placeholder="Dublin"
                value={formData.shippingAddress.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />

              {/* County and Eircode Row */}
              <div className="address-row">
                <div className="address-row-item">
                  <Input
                    label="County"
                    type="text"
                    placeholder="Dublin"
                    value={formData.shippingAddress.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                  />
                </div>
                <div className="address-row-item">
                  <Input
                    label="Eircode"
                    type="text"
                    placeholder="D02 XY45"
                    value={formData.shippingAddress.zipCode}
                    onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="edit-dialog-footer">
          <button className="edit-dialog-button-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="edit-dialog-button-save"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfileDialog;
